// 📦 External Libraries
import { useEffect, useState, useRef } from "react"
import { Grid } from "@mui/material"
import { useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import moment from "moment"

import {
  capitalizeFirstLetter,
  colorsMap,
  calculateLabel,
  status,
} from "utilities/assistantUtil"

import TodoListLXOMyTask from "components/TodoListLXOMyTask/TodoListLXOMyTask"
import PieChartLXOMyTask from "components/PieChartLXOMyTask/PieChartLXOMyTask"

// 🧠 Hooks

// 🔄 Redux
import {
  useLazyGetDashboardLXOMyTaskQuery,
  useLazyGetReportLXOMyTaskQuery,
} from "shared/redux/endpoints/dashboardMytaskLXO"
import { selectAuth } from "shared/redux/slices/authSlice"

// 💅 Styles
import makeStyles from "pages/style"
import { myTasksContainerStyle, myTasksStyle } from "./style"

// ⚙️ Constants / Utils / Mock Data
import { useTasks } from "pages/assistant/hooks/useTasks"
import { renderBackgroundColorLXO } from "shared/utils/common"
import { from } from "stylis"

function MyTasksLXO() {
  // ==============================
  // 📦 Imports & Hooks
  // ==============================
  const classes = makeStyles()
  const [searchParams, setSearchParams] = useSearchParams()
  const auth = useSelector(selectAuth)
  const role = auth?.role
  const centerId = useSelector((state) => state.auth.centerId)

  // ==============================
  // 🌐 URL Params
  // ==============================
  const id = searchParams.get("id")
  const type = searchParams.get("type")
  const start = searchParams.get("start")
  const end = searchParams.get("end")

  // ==============================
  // 🧠 State Management
  // ==============================
  const [isOpenDetail, setOpenDetail] = useState(false)
  const [selectedType, setType] = useState("")
  const [dataFeedback, setDataFeedback] = useState({})
  const [reportId, setReportId] = useState(null)
  const [isHasValueReportId, setHasValueReportId] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const [dataDetail, setDataDetail] = useState({})
  const [tabIndex, setTabIndex] = useState(0)
  const [, setStart] = useState(null)
  const [, setEnd] = useState(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [state, setState] = useState({
    page: 0,
    size: 10,
    centerId,
    from: start,
    to: end,
    taskType: "",
  })

  const tabs = [
    { value: 0, label: "All", type: "", key: "" },
    { value: 1, label: "Scheduling", type: "scheduling", key: "SCHEDULING" },
    { value: 2, label: "Planning", type: "planning", key: "PLANNING" },
    { value: 3, label: "Reporting", type: "reporting", key: "REPORTING" },
  ]

  // ==============================
  // ⚙️ API Hooks
  // ==============================
  const [triggerDashboard, { data: dataReport, isFetching: isFetchingReport }] =
    useLazyGetReportLXOMyTaskQuery()
  const [trigger, { data, isFetching: isFetchingPercent }] =
    useLazyGetDashboardLXOMyTaskQuery()

  // ==============================
  // 📊 Derived Data from Dashboard
  // ==============================

  const totalPage = data?.data?.totalPages
  const currentPage = data?.data?.pageable?.pageNumber

  // ==============================
  // 📥 Custom Hook - Tasks
  // ==============================

  const calculatePercentByKey = (section, key) => {
    if (!section?.status) return 0

    const {
      completedOnTime = 0,
      completedOverdue = 0,
      inProgress = 0,
      overdue = 0,
    } = section.status

    const total = completedOnTime + completedOverdue + inProgress + overdue
    if (total === 0) return 0

    const value = section.status[key] || 0
    return Math.round((value / total) * 100)
  }

  const makePieData = (section) => [
    {
      id: 0,
      value: section?.status?.completedOnTime || 0,
      label: "Completed On Time",
      color: renderBackgroundColorLXO("completedOnTime"),
      percent: calculatePercentByKey(section, "completedOnTime") || 0,
      key: "completedOnTime",
    },
    {
      id: 1,
      value: section?.status?.completedOverdue || 0,
      label: "Completed Overdue",
      color: renderBackgroundColorLXO("completedOverdue"),
      percent: calculatePercentByKey(section, "completedOverdue") || 0,
      key: "completedOverdue",
    },
    {
      id: 2,
      value: section?.status?.inProgress || 0,
      label: "In Progress",
      percent: calculatePercentByKey(section, "inProgress") || 0,
      color: renderBackgroundColorLXO("inProgress"),
      key: "inProgress",
    },
    {
      id: 3,
      value: section?.status?.overdue || 0,
      label: "Overdue",
      color: renderBackgroundColorLXO("overdue"),
      percent: calculatePercentByKey(section, "overdue") || 0,
      key: "overdue",
    },
  ]

  useEffect(() => {
    if (!centerId || (!start && !end)) return
    const startI = moment(start || state.from, "MM-DD-YYYY").add(7, "hours") // Điều chỉnh thời gian bắt đầu để bao gồm cả ngày đầu tiên
    const endI = moment(end || state.to, "MM-DD-YYYY").add(1, "day") // Điều chỉnh thời gian kết thúc để bao gồm cả ngày cuối cùng

    const isoStart = startI.toISOString()
    const isoEnd = endI.toISOString()
    console.log(startI, "startI")
    console.log(endI, "endI")
    console.log(moment(startI).format("MM-DD-YYYY"))

    trigger({
      ...state,
      centerId,
      from: isoStart,
      to: isoEnd,
    })
  }, [centerId, start, end])

  const dataRenderPieChart = [
    {
      label: "Planning",
      ...(dataReport?.data?.planning || {}),
      labelChart: `${dataReport?.data?.planning?.finished}/${dataReport?.data?.planning?.total}`,
      tasksPieChart: {
        data: makePieData({ ...dataReport?.data?.planning }),
        label: `${dataReport?.data?.planning?.finished || 0}/${
          dataReport?.data?.planning?.total || 0
        }`,
        overdue: dataReport?.data?.planning?.overdue || 0,
        onCompleteToday: dataReport?.data?.planning?.toCompleteToday || 0,
      },
    },
    {
      label: "Scheduling",
      ...(dataReport?.data?.scheduling || {}),
      labelChart: `${dataReport?.data?.scheduling?.finished}/${dataReport?.data?.scheduling?.total}`,
      tasksPieChart: {
        data: makePieData({
          ...dataReport?.data?.scheduling,
          label: "Scheduling",
        }),
        label: `${dataReport?.data?.scheduling?.finished || 0}/${
          dataReport?.data?.scheduling?.total || 0
        }`,
        overdue: dataReport?.data?.scheduling?.overdue || 0,
        onCompleteToday: dataReport?.data?.scheduling?.toCompleteToday || 0,
      },
    },
    {
      label: `Reporting`,
      ...(dataReport?.data?.reporting || {}),
      labelChart: `${dataReport?.data?.reporting?.finished}/${dataReport?.data?.reporting?.total}`,
      tasksPieChart: {
        data: makePieData({
          ...dataReport?.data?.reporting,
          label: "Reporting",
        }),
        label: `${dataReport?.data?.reporting?.finished || 0}/${
          dataReport?.data?.reporting?.total || 0
        }`,
        overdue: dataReport?.data?.reporting?.overdue || 0,
        onCompleteToday: dataReport?.data?.reporting?.toCompleteToday || 0,
      },
    },
  ]

  // label:

  // ==============================
  // 🧩 Effects
  // ==============================

  // Cuộn tới phần tử có id tương ứng sau khi đã có reportId
  useEffect(() => {
    if (isHasValueReportId) {
      setTimeout(() => {
        const scrollableDiv = document.getElementById("id" + reportId)
        scrollableDiv?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }, 1000)
    }
  }, [isHasValueReportId])

  // Khi có query params, tìm item tương ứng và mở chi tiết

  // Gọi dashboard khi load
  useEffect(() => {
    triggerDashboard({
      centerId,
    }).then((result) => {})
  }, [])

  // ==============================
  // 🖥️ Responsive & Scrolling
  // ==============================
  const contentRef = useRef(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true)
      if (contentRef) {
        clearTimeout(contentRef?.current?.timeout)
        contentRef.current.timeout = setTimeout(() => {
          setIsScrolling(false)
        }, 500)
      }
    }

    const content = contentRef.current
    content?.addEventListener("scroll", handleScroll)
    return () => content?.removeEventListener("scroll", handleScroll)
  }, [])

  // ==============================
  // 🔢 Pagination
  // ==============================
  const pageRange = Array.from({ length: totalPage }, (_, i) => i + 1)
  const filteredPages = pageRange.slice(
    Math.max(currentPage - 2, 0),
    Math.min(currentPage + 3, totalPage + 1)
  )

  const handlerListData = async (params = {}, options = {}) => {
    const nextState = { ...state, ...params }
    const startI = moment(nextState.from || start, "MM-DD-YYYY").add(7, "hours") // Điều chỉnh thời gian bắt đầu để bao gồm cả ngày đầu tiên
    const endI = moment(nextState.to || end, "MM-DD-YYYY").add(1, "day") // Điều chỉnh thời gian kết thúc để bao gồm cả ngày cuối cùng

    const isoStart = startI.toISOString()
    const isoEnd = endI.toISOString()

    const result = await trigger({
      ...nextState,
      from: isoStart,
      to: isoEnd,
    })
    setState(nextState)

    if (options?.syncDetail && options?.taskId) {
      const list = result?.data?.data?.content || []
      const updated = list.find((item) => item.taskId === options.taskId)
      if (updated) {
        setDataDetail(updated)
      }
    }
    return result
  }

  // ==============================
  // 🔁 Utility Functions
  // ==============================
  const sortList = (list) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const getDeadlineStatus = (item) => {
      const deadline = new Date(
        item?.task?.deadlineDate ?? item?.feedback?.deadlineDate
      )
      deadline.setHours(0, 0, 0, 0)

      if (deadline < today) return "passed"
      if (deadline.getTime() === today.getTime()) return "today"
      return "upcoming"
    }

    list.sort((a, b) => {
      const [statusA, dateA] = [
        getDeadlineStatus(a),
        new Date(a?.task?.deadlineDate ?? a?.feedback?.deadlineDate).getTime(),
      ]
      const [statusB, dateB] = [
        getDeadlineStatus(b),
        new Date(b?.task?.deadlineDate ?? b?.feedback?.deadlineDate).getTime(),
      ]

      if (statusA !== statusB) return statusA === "passed" ? -1 : 1
      return dateB - dateA
    })

    return list
  }

  const propsTodoListLXOMyTask = {
    classes,
    start,
    end,
    tabs,
    isOpenDetail,
    contentRef,
    list: data?.data?.content || [],
    filteredPages,
    tabIndex,
    isScrolling,
    currentPage,
    totalPage,
    setStart,
    searchParams,
    setSearchParams,
    setEnd,
    setTabIndex,
    setOpenDetail,
    sortList,
    setReportId,
    setDataDetail,
    dataDetail,
    isFetchingReport,
    page,
    setPage,
    setState,
    state,
    handlerList: handlerListData,
    refetch: (options = {}) => handlerListData({}, options),
  }

  const propsPieChartLXOMyTask = {
    myTasksContainerStyle,
    classes,
    loading,
    isFetchingReport,
  }

  return (
    <div className="assistant-my-tasks">
      {role !== "CSO" ? (
        <Grid
          container
          direction="row"
          alignItems="stretch"
          columnSpacing={{ xs: 1.5, xxl: 2, xxxl: 3 }}
          sx={myTasksStyle}
          className="my-tasks__pie-chart-section"
        >
          {dataRenderPieChart &&
            dataRenderPieChart.length > 0 &&
            dataRenderPieChart.map((item, index) => {
              const propsItem = {
                ...item,
                taskOverdue: item.overdue,
                taskOnCompleteToday: item.toCompleteToday,
              }
              return (
                <Grid
                  container
                  item
                  xs={12}
                  lg={6}
                  xxl={4}
                  gap={1.5}
                  key={index}
                >
                  <PieChartLXOMyTask
                    {...propsPieChartLXOMyTask}
                    {...propsItem}
                  />
                </Grid>
              )
            })}
        </Grid>
      ) : null}

      <TodoListLXOMyTask {...propsTodoListLXOMyTask} />
    </div>
  )
}

export default MyTasksLXO
